/* eslint-disable react/prop-types */
import React, { useState, useContext } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { boolean, object, string } from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { login } from '../redux';
import { useUpdateBAAStatus } from '../api';
import {
  CheckboxField,
  ContentForRequiredScroll,
  PublicLayout,
  TextField,
  ResourceContent,
  FormWizardPageChangeContext,
  ResourcePublicLayout
} from '../components';
import { AnyType, Breadcrumb, GeneralObject } from '../interfaces';
import {
  rawSafeHtmlProps,
  htmlProps,
  clientApplicationNameSelector,
  localDateFormat,
  FORM_WIZARD_VALUES,
  ROUTE_PATHS,
  getConfigurationDataKey,
  clientIdSelector
} from '../utils';
import { AppRootState } from '../redux';
import { FORM_ERROR } from 'final-form';

interface BAAViewProps {
  loginResponse?: any;
  userName: string;
}

export const baaSchema = object({
  acknowledge_baa: boolean().oneOf([true], 'Must Accept BAA').default(undefined).required(),
  acknowledge_language: boolean().oneOf([true], 'Must Accept Agreement').default(undefined).required(),
  signature: string().required().default(''),
  signature_date: string()
    .required()
    .default(localDateFormat(new Date()) as string)
});

const REGISTRATION_FORM_PAGE_NAMES = FORM_WIZARD_VALUES?.pages?.registration?.names;

export const BAAView: React.FC<BAAViewProps> = ({ loginResponse, userName }) => {
  const dispatch = useDispatch();
  const [isBottom, setIsBottom] = useState(false);
  //const applicationName = useSelector(clientApplicationNameSelector);
  const { mutate: updateBAAStatus } = useUpdateBAAStatus({}); // initialize updateBAAStatus
  const programId: any = useSelector(clientIdSelector);
  const [isChecked, setIsChecked] = useState(false);

  const onScrollToBottom = () => {
    if (!isBottom) setIsBottom(true);
  };
  const handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked);
  };

  const changeFormWizardPage = useContext(FormWizardPageChangeContext);
  const baaLegal = useSelector(
    (state: AppRootState) =>
      (((state.app?.entities?.resources as AnyType) ?? {})['registration.baa.legal' ?? ''] as AnyType)?.data
  );

  const handleClick = async () => {
    try {
      await updateBAAStatus({ userName, programId });
      await dispatch(login(loginResponse));
    } catch (error: any) {
      return { [FORM_ERROR]: error.message };
    }
  };

  return (
    <ResourcePublicLayout customHeader={true}>
      <div className='container-fluid px-5'>
        <div className='border rounded p-4 mt-3 mb-3 shadow-sm'>
          <Row className='justify-content-between'>
            <Col>
              <h5>Read and Acknowledge the Following Documents</h5>
            </Col>
            <Col>
              <p className='required-text float-end general-note mb-2'>
                All fields are <u>required</u> unless marked optional.
              </p>
            </Col>
            <p>
              Please confirm you have reviewed the Agreements below to continue. You must scroll to the bottom to enable
              the acknowledgement checkmark.
            </p>
          </Row>

          <ContentForRequiredScroll
            onScrollToBottom={onScrollToBottom}
            className='border border-gray rounded py-3 pl-3 pr-5 fs-2'
            style={{ maxWidth: '1500px', maxHeight: '300px' }}
            data-testid='cmx__baa-content'>
            <p className='mb-0'>
              <span {...htmlProps(baaLegal)} />
            </p>
          </ContentForRequiredScroll>

          <p className='required-text mt-2'>
            You must scroll and read the entirety of the Terms and Conditions to proceed.
          </p>

          <Row className='align-items-center ml-1 py-2'>
            <Form.Group controlId='acknowledge_baa'>
              <Form.Check
                type='checkbox'
                label='By checking this box and typing my signature below, I am acknowledging that I have read, understood, and agree to the terms of this Business Associate Agreement. I further represent that I have the authority to enter into this Business Associate Agreement on behalf of the provider and to bind the provider to this Agreement.'
                disabled={!isBottom}
                checked={isChecked}
                onChange={handleCheckChange}
              />
            </Form.Group>
          </Row>

          <Row>
            <Col className='text-end mb-4'>
              <Button
                className='text-nowrap'
                variant='secondary-new'
                disabled={!isBottom || !isChecked}
                size='sm'
                onClick={handleClick}
                data-testid='cmx__new-request-button'>
                Continue to Portal
                <i className='fa-light fa-circle-arrow-right ms-2' />
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    </ResourcePublicLayout>
  );
};
